import { getConfig } from '@superbet-group/web.lib.config';
import { Locale } from '@superbet-group/web.types';
import Vue from 'vue';
import VueI18n, {
    DateTimeFormat,
    NumberFormat,
    type LocaleMessageObject,
} from 'vue-i18n';

import {
    PluralizationFunction,
    selectPluralTranslation as slavicSelectPluralTranslation,
} from '@app/localization/translations/slavicPluralization';
import { getIsPrerender } from '@src/app/shared/featureFlags/prerender';

Vue.use(VueI18n);

const mappings: { [k: string]: Intl.DateTimeFormatOptions} = {
    w: { weekday: 'short' },
    W: { weekday: 'long' },

    dd: { day: 'numeric' },
    m: { month: 'narrow' },
    mm: { month: 'numeric' },
    month: { month: 'short' },
    MONTH: { month: 'long' },
    yy: { year: '2-digit' },
    yyyy: { year: 'numeric' },

    HH: { hour: 'numeric' },
    MM: { minute: 'numeric' },
    SS: { second: 'numeric' },
};

const buildMapping = (format: string[]) => {
    return format.reduce((acc: any, f) => ({ ...acc, ...(<any>mappings)[f] }), {});
};

const dateTimeFormat: DateTimeFormat = {
    ddMMyyyy: buildMapping(['dd', 'month', 'yyyy']),
    ddmmyyyy: buildMapping(['dd', 'mm', 'yyyy']),
    HHMM: buildMapping(['HH', 'MM']),
    HHMMSS: buildMapping(['HH', 'MM', 'SS']),
    Wddmmyy: buildMapping(['W', 'dd', 'mm', 'yy']),
    fullDate: buildMapping(['W', 'dd', 'MONTH', 'yy']),
    wHHMM: buildMapping(['w', 'HH', 'MM']),
    wddHHMM: buildMapping(['w', 'dd', 'HH', 'MM']),
    ddmm: buildMapping(['dd', 'mm']),
    ddmmyyyyHHMM: buildMapping(['dd', 'mm', 'yyyy', 'HH', 'MM']),
    monthddHHMM: buildMapping(['month', 'dd', 'HH', 'MM']),
    monthddHHMMyyyy: buildMapping(['month', 'dd', 'HH', 'MM', 'yyyy']),
    ddmmyyHHMM: buildMapping(['dd', 'mm', 'yy', 'HH', 'MM']),
    wddm: buildMapping(['w', 'dd', 'm']),
    wddmm: buildMapping(['w', 'dd', 'mm']),
    ddmonth: buildMapping(['dd', 'month']),
    ddmonthyyyy: buildMapping(['dd', 'month', 'yyyy']),
    w: buildMapping(['w']),
    W: buildMapping(['W']),
    wdd: buildMapping(['w', 'dd']),
    time: buildMapping(['HH', 'MM']),
    MMYYYY: buildMapping(['MONTH', 'yyyy']),
    ddMMYYYY: buildMapping(['dd', 'MONTH', 'yyyy']),
    MM: buildMapping(['MONTH']),
    yyyy: buildMapping(['yyyy']),
    ddMONTH: buildMapping(['dd', 'MONTH']),
};

const numberFormats: Record<Locale, NumberFormat> = {
    [Locale.English]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'EUR',
        },
    },
    [Locale.Romanian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'RON',
        },
    },
    [Locale.Polish]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'PLN',
        },
    },
    [Locale.Serbian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency',
            currency: 'RSD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    },
    [Locale.EnglishBelgian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'EUR',
        },
    },
    [Locale.DutchBelgian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'EUR',
        },
    },
    [Locale.FrenchBelgian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'EUR',
        },
    },
    [Locale.PortugueseBrazilian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'BRL',
        },
    },
    [Locale.EnglishBrazilian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'BRL',
        },
    },
    [Locale.Bulgarian]: {
        decimal: { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 },
        currency: {
            style: 'currency', currency: 'Лев',
        },
    },
};

const dateTimeFormats: Record<Locale, DateTimeFormat> = {
    [Locale.English]: dateTimeFormat,
    [Locale.Romanian]: dateTimeFormat,
    [Locale.Polish]: dateTimeFormat,
    [Locale.Serbian]: dateTimeFormat,
    [Locale.EnglishBelgian]: dateTimeFormat,
    [Locale.DutchBelgian]: dateTimeFormat,
    [Locale.FrenchBelgian]: dateTimeFormat,
    [Locale.PortugueseBrazilian]: dateTimeFormat,
    [Locale.Bulgarian]: dateTimeFormat,
    /**
     * Intentionally leaving "en-BR" blank so it falls back on en-GB default.
     * "en-BR" is not recognised by Intl.DateTimeFormat, so it falls back on "en",
     * which uses mmddyyyy format.
     */
    [Locale.EnglishBrazilian]: {},
};

type PluralizableLocale = Extract<Locale, Locale.Serbian | Locale.Polish>;

const pluralizationRules: Record<PluralizableLocale, PluralizationFunction> = {
    [Locale.Serbian]: slavicSelectPluralTranslation,
    [Locale.Polish]: slavicSelectPluralTranslation,
};

const i18n = new VueI18n({
    numberFormats,
    dateTimeFormats,
    locale: getConfig().appLocale.locale,
    fallbackLocale: Locale.English,
    pluralizationRules,
    silentTranslationWarn: true,
});

/**
    if we want to support out-of-shell components (ex. storybook), this reducer would probably need to go
    inside our DS lib and also manage fetching of localisations on its own in case they are not present on
    the window object at the point in time the components load. then, we can import the reducer from that lib
    and use it here not to repeat ourselves. for now - this just filters design system keys and makes them
    available for the useSdsTranslations() composable that serves as a nicer way to implement DS specific translations
    so components don't have to worry about where these come from. It also makes sure components are not aware
    of anything from the global window
*/
function generateSdsLocalisations(translations: LocaleMessageObject) {
    window.sdsTranslationMessages = Object.keys(translations).filter((key) => key.startsWith('sds')).reduce((obj, key) => {
        // eslint-disable-next-line no-param-reassign
        obj[key] = translations[key];
        return obj;
    }, {} as LocaleMessageObject);
}

function setTranslations(override = false) {
    if (override) {
        i18n.setLocaleMessage(i18n.locale, window.translationMessages);
    } else {
        i18n.mergeLocaleMessage(i18n.locale, window.translationMessages);
    }
    generateSdsLocalisations(window.translationMessages);
    window.translationMessages = {};
}

setTranslations();

async function forceFetchTranslations() {
    const translationBaseUrl = document.getElementById('translations-script')?.getAttribute('src');

    if (translationBaseUrl) {
        let url: URL;
        try {
            url = new URL(translationBaseUrl);
        } catch {
            url = new URL(`${window.location.origin}${translationBaseUrl}`);
        }

        url.searchParams.delete('locale');
        url.searchParams.set('force', i18n.locale);

        try {
            const translationsScript = await (await fetch(url.toString())).text();

            // eslint-disable-next-line no-eval
            eval(translationsScript.replace('var translations', 'window.translationMessages'));

            setTranslations(true);
        } catch {
            // do nothing
        }
    }
}

if (getIsPrerender()) {
    forceFetchTranslations();
}

export default i18n;
